/**
 * jobs.stats_json has FOUR writer shapes plus two non-shapes, and the API
 * passes the column through AS STORED (api/src/adminRoutes.ts:206-222). This
 * module is the only place that decides which is which; JobStatsCell renders
 * whatever it returns.
 *
 * The four writers, all merged today:
 *   1. queue Run-Stats      markJobDone/markJobError   (jobs/jobStore.ts:20-28)
 *   2. action Run-Stats     runActionBody              (adminRoutes.ts:1453-1463)
 *      = shape 1 plus `result`, which is itself bounded and may be
 *        { omitted: 'unserializable' } or { omitted: 'too_large', bytes }
 *   3. skipped              markJobSkipped             (jobStore.ts:81-84)
 *      status is 'done' -- a skip is not a failure
 *   4. postponed            markJobPostponed           (jobStore.ts:91-100)
 *      status STAYS 'queued' -- the row is still waiting
 * Plus: null (a fresh row) and a raw string (parseStoredStats' unparseable
 * fallback, which exists so one bad row cannot take a listing down).
 *
 * DISCRIMINATION ORDER MATTERS. `skipped` and `postponed` are checked before
 * the run-stats shape because they are the narrow ones; checking `sheetsCalls`
 * first would misread nothing today but would silently swallow a future writer
 * that adds a counter to a postponement payload.
 *
 * NOTHING HERE FALLS THROUGH TO A PLAUSIBLE DEFAULT. An unrecognised object
 * becomes `raw`, never `none`: "there is nothing to show" and "there is
 * something here I do not recognise" are different statements, and a monitor
 * that renders the second as the first is the exact misreporting this module
 * exists to prevent. Every branch is driven by test/jobStats.test.ts and every
 * branch is rendered by DOM suite 3 of 3.
 */

export type JobStatsKind = 'none' | 'skipped' | 'postponed' | 'run' | 'raw';

export type JobStatsView =
  | { kind: 'none' }
  | { kind: 'skipped'; reason: string }
  | { kind: 'postponed'; postponements: number; postponedUntil: string; reason: string }
  | {
      kind: 'run';
      sheetsCalls: number; ebayCalls: number; durationMs: number;
      attempt: number; postponements: number;
      /** Present only for admin-triggered actions (writer 2). */
      result?: unknown;
    }
  | { kind: 'raw'; text: string };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * `JSON.stringify` is not total: it THROWS on a BigInt and answers `undefined`
 * for a function, a symbol or `undefined` itself. `stats` is typed `unknown`
 * and this module is exported, so neither is merely theoretical -- and either
 * one reaching the renderer as `undefined` would paint an EMPTY details cell
 * over a row that has something in it, which is the same misreport as a
 * silent `none`.
 */
function safeJson(value: unknown, space?: number): string {
  try {
    const json = JSON.stringify(value, null, space);
    return json === undefined ? String(value) : json;
  } catch {
    return String(value);
  }
}

export function describeStats(stats: unknown): JobStatsView {
  if (stats === null || stats === undefined) return { kind: 'none' };
  // The unparseable-row fallback: parseStoredStats hands back the raw string.
  if (typeof stats === 'string') return { kind: 'raw', text: stats };
  if (!isRecord(stats)) return { kind: 'raw', text: safeJson(stats) };

  if ('skipped' in stats) return { kind: 'skipped', reason: str(stats.skipped) };
  if ('postponedUntil' in stats) {
    return {
      kind: 'postponed',
      postponements: num(stats.postponements),
      postponedUntil: str(stats.postponedUntil),
      reason: str(stats.reason),
    };
  }
  if ('sheetsCalls' in stats || 'durationMs' in stats) {
    return {
      kind: 'run',
      sheetsCalls: num(stats.sheetsCalls),
      ebayCalls: num(stats.ebayCalls),
      durationMs: num(stats.durationMs),
      attempt: num(stats.attempt),
      postponements: num(stats.postponements),
      ...('result' in stats ? { result: stats.result } : {}),
    };
  }
  // An unrecognised object is shown as JSON rather than dropped: a new writer
  // must be visible in the monitor from the day it ships, not from the day
  // someone notices a blank column.
  return { kind: 'raw', text: safeJson(stats) };
}

/**
 * `runActionBody`'s `result` field, which is the job body's own summary and
 * therefore of no fixed shape at all. `actionResult` (adminRoutes.ts:1369-1381)
 * bounds it to one of exactly four things before it is ever stored, and this
 * mirrors that bound:
 *
 *   - `null`      -- "nothing worth keeping" (undefined/null, or a value
 *                    JSON.stringify answered `undefined` for)
 *   - `{ omitted: 'unserializable' }`    -- stringify THREW on it
 *   - `{ omitted: 'too_large', bytes }`  -- over ACTION_RESULT_MAX_LEN
 *   - anything else -- a JSON round-trip of the body's own object
 *
 * The two `omitted` markers get their own copy rather than being pretty-printed
 * as `{"omitted":"too_large"}`: they are statements ABOUT the missing summary,
 * not the summary, and an operator reading the raw marker would reasonably
 * conclude the job returned a field called `omitted`.
 */
export type JobResultView =
  | { kind: 'absent' }
  | { kind: 'omittedUnserializable' }
  | { kind: 'omittedTooLarge'; bytes: number }
  | { kind: 'json'; text: string };

export function describeResult(result: unknown): JobResultView {
  if (result === null || result === undefined) return { kind: 'absent' };
  if (isRecord(result) && typeof result.omitted === 'string') {
    if (result.omitted === 'unserializable') return { kind: 'omittedUnserializable' };
    if (result.omitted === 'too_large') return { kind: 'omittedTooLarge', bytes: num(result.bytes) };
  }
  return { kind: 'json', text: safeJson(result, 2) };
}

/** The `raw` cell shows at most this much inline; the FULL value travels in
 *  the `title` attribute, the same split lib/jobDisplay.ts's `truncateError`
 *  makes for the error column. */
export const RAW_STATS_MAX_LEN = 200;

export function truncateRaw(text: string, maxLength = RAW_STATS_MAX_LEN): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
}
